import React, { useState, useEffect } from 'react';
import api from '../api';
import { OPERATIONS_TABS } from '../config/pages';
import { useAuth } from '../AuthContext';

export default function Settings() {
  const { tenant, user, logout, can, canTab, startViewAs } = useAuth();
  const [activeTab, setActiveTab] = useState('branding');

  const ALL_TABS = [
    { id: 'branding',  icon: '🎨', label: 'Branding' },
    { id: 'general',   icon: '⚙️', label: 'General Settings' },
    { id: 'workweek',  icon: '📅', label: 'Work Week' },
    { id: 'security',  icon: '🔐', label: 'Security' },
    { id: 'communication', icon: '📧', label: 'Communication' },
    ...(user?.role === 'owner' ? [{ id: 'danger', icon: '⚠️', label: 'Danger Zone' }] : []),
  ];
  const TABS = ALL_TABS.filter(t => {
    const cfg = (OPERATIONS_TABS.settings || []).find(x => x.id === t.id);
    return !cfg?.permissionKey || canTab(cfg.permissionKey);
  });

  // If current tab isn't in the permitted list, switch to first available
  useEffect(() => {
    if (TABS.length && !TABS.find(t => t.id === activeTab)) {
      setActiveTab(TABS[0].id);
    }
  }, [TABS.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page-wrap">
      {/* Tab bar */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'var(--card)', padding:6, borderRadius:10, border:'1px solid var(--border)', flexWrap:'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding:'8px 16px', border:'none', borderRadius:6, cursor:'pointer',
              fontSize:'0.82rem', fontWeight:600,
              background: activeTab === t.id ? '#4f46e5' : 'transparent',
              color: activeTab === t.id ? '#fff' : 'var(--text-muted)',
              transition:'all 0.15s',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'branding'  && <BrandingTab  tenant={tenant} />}
      {activeTab === 'general'   && <GeneralTab   tenant={tenant} />}
      {activeTab === 'workweek'  && <WorkWeekTab  tenant={tenant} />}
      {activeTab === 'security'  && <SecurityTab  user={user} logout={logout} can={can} startViewAs={startViewAs} />}
      {activeTab === 'communication' && <CommunicationTab tenant={tenant} />}
      {activeTab === 'danger' && user?.role === 'owner' && <DangerZoneTab tenant={tenant} user={user} logout={logout} />}
    </div>
  );
}

/* ── Branding ── */
function BrandingTab({ tenant }) {
  const [form, setForm] = useState({
    brand_name:     tenant?.brand_name     || '',
    brand_tagline:  tenant?.brand_tagline  || '',
    brand_logo_url: tenant?.brand_logo     || '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState({ text:'', ok:true });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/tenant/branding', form);
      setMsg({ text:'Branding updated successfully.', ok:true });
    } catch {
      setMsg({ text:'Failed to update branding.', ok:false });
    } finally { setSaving(false); }
  };

  return (
    <div className="card">
      <div className="card-header"><div className="card-title">🎨 Agency Branding</div></div>
      {msg.text && <div className={msg.ok ? 'alert alert-success' : 'alert alert-error'} style={alertStyle(msg.ok)}>{msg.text}</div>}
      <form onSubmit={save}>
        <div className="form-group">
          <label className="form-label">Agency / Brand Name</label>
          <input className="form-input" value={form.brand_name}
            onChange={e => setForm({...form, brand_name: e.target.value})}
            placeholder="One Hundred Technologies" />
        </div>
        <div className="form-group">
          <label className="form-label">Tagline</label>
          <input className="form-input" value={form.brand_tagline}
            onChange={e => setForm({...form, brand_tagline: e.target.value})}
            placeholder="Your agency tagline..." />
        </div>
        <div className="form-group">
          <label className="form-label">Logo URL</label>
          <input className="form-input" value={form.brand_logo_url}
            onChange={e => setForm({...form, brand_logo_url: e.target.value})}
            placeholder="https://... (URL to your logo image)" />
          {form.brand_logo_url && (
            <div style={{ marginTop:10, padding:10, background:'var(--bg)', borderRadius:8, border:'1px solid var(--border)', display:'inline-block' }}>
              <img src={form.brand_logo_url} alt="Logo preview" style={{ height:50, objectFit:'contain' }}
                onError={e => e.target.style.display='none'} />
            </div>
          )}
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </form>
    </div>
  );
}

/* ── General ── */
function GeneralTab({ tenant }) {
  const [form, setForm] = useState({
    timezone:    tenant?.timezone    || 'Asia/Kolkata',
    currency:    tenant?.currency    || 'INR',
    date_format: tenant?.date_format || 'DD-MM-YYYY',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState({ text:'', ok:true });

  const TIMEZONES    = ['Asia/Kolkata','Asia/Dubai','Asia/Singapore','Europe/London','America/New_York','America/Los_Angeles'];
  const CURRENCIES   = ['INR','USD','EUR','GBP','AED','SGD'];
  const DATE_FORMATS = ['DD-MM-YYYY','MM-DD-YYYY','YYYY-MM-DD'];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/tenant/settings', form);
      setMsg({ text:'Settings updated successfully.', ok:true });
    } catch {
      setMsg({ text:'Failed to update settings.', ok:false });
    } finally { setSaving(false); }
  };

  return (
    <div className="card">
      <div className="card-header"><div className="card-title">⚙️ General Settings</div></div>
      {msg.text && <div style={alertStyle(msg.ok)}>{msg.text}</div>}
      <form onSubmit={save}>
        <div className="form-group">
          <label className="form-label">Timezone</label>
          <select className="form-select" value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})}>
            {TIMEZONES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Currency</label>
          <select className="form-select" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Date Format</label>
          <select className="form-select" value={form.date_format} onChange={e => setForm({...form, date_format: e.target.value})}>
            {DATE_FORMATS.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}

/* ── Work Week ── */
function WorkWeekTab({ tenant }) {
  const DAYS = [
    { id:0, short:'Sun', label:'Sunday' },
    { id:1, short:'Mon', label:'Monday' },
    { id:2, short:'Tue', label:'Tuesday' },
    { id:3, short:'Wed', label:'Wednesday' },
    { id:4, short:'Thu', label:'Thursday' },
    { id:5, short:'Fri', label:'Friday' },
    { id:6, short:'Sat', label:'Saturday' },
  ];

  const [weeklyOff, setWeeklyOff] = useState(tenant?.weekly_off || [0, 6]);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState({ text:'', ok:true });

  const toggle = (id) => setWeeklyOff(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/settings', { weekly_off: weeklyOff });
      setMsg({ text:'Work week updated successfully.', ok:true });
    } catch {
      setMsg({ text:'Failed to update.', ok:false });
    } finally { setSaving(false); }
  };

  return (
    <div className="card">
      <div className="card-header"><div className="card-title">📅 Work Week Configuration</div></div>
      <div className="text-sm text-muted" style={{ marginBottom:16 }}>
        Select the days that are <strong>OFF</strong> (non-working). These are excluded from deadline calculations.
      </div>
      {msg.text && <div style={alertStyle(msg.ok)}>{msg.text}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:8, marginBottom:20 }}>
        {DAYS.map(d => {
          const isOff = weeklyOff.includes(d.id);
          return (
            <button key={d.id} type="button" onClick={() => toggle(d.id)} style={{
              padding:'12px 4px', border:`1px solid ${isOff ? '#ef4444' : '#22c55e'}`,
              borderRadius:8, cursor:'pointer', textAlign:'center',
              background: isOff ? '#fef2f2' : '#f0fdf4',
            }}>
              <div style={{ fontWeight:700, fontSize:'0.8rem', color:'var(--text)', marginBottom:4 }}>{d.short}</div>
              <div style={{ fontSize:'0.7rem', color: isOff ? '#ef4444' : '#22c55e' }}>
                {isOff ? '🔴 Off' : '🟢 On'}
              </div>
            </button>
          );
        })}
      </div>

      <button className="btn btn-primary" onClick={save} disabled={saving}>
        {saving ? 'Saving...' : 'Save Work Week'}
      </button>
    </div>
  );
}

/* ── Security ── */
function SecurityTab({ user, logout, can, startViewAs }) {
  /* Own password */
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg]       = useState({ text:'', ok:true });
  const [showPw, setShowPw]     = useState({ current:false, newpw:false, confirm:false });

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password)
      return setPwMsg({ text:'New passwords do not match.', ok:false });
    if (pwForm.new_password.length < 8)
      return setPwMsg({ text:'Password must be at least 8 characters.', ok:false });
    setPwSaving(true);
    try {
      await api.put('/auth/change-password', {
        current_password:         pwForm.current_password,
        new_password:             pwForm.new_password,
        new_password_confirmation: pwForm.confirm_password,
      });
      setPwMsg({ text:'Password changed. Logging you out in 2 seconds...', ok:true }); setTimeout(() => logout(), 2000);
      setPwForm({ current_password:'', new_password:'', confirm_password:'' });
    } catch (err) {
      setPwMsg({ text: err?.response?.data?.message || 'Failed to change password.', ok:false });
    } finally { setPwSaving(false); }
  };

  /* Change email — owners only */
  const [newEmail, setNewEmail]   = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg]   = useState({ text:'', ok:true });

  const changeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    if (newEmail.trim() === user?.email)
      return setEmailMsg({ text:'That is already your current email address.', ok:false });
    setEmailSaving(true);
    try {
      const res = await api.put('/auth/profile', { email: newEmail.trim() });
      if (res.data?.email_changed) {
        setEmailMsg({ text:'Email updated. Logging you out — please sign in with your new email.', ok:true });
        setTimeout(() => logout(), 2500);
      }
    } catch (err) {
      const msg = err?.response?.data?.errors?.email?.[0]
        || err?.response?.data?.message
        || 'Failed to update email.';
      setEmailMsg({ text: msg, ok:false });
    } finally { setEmailSaving(false); }
  };

  /* Team credentials */
  const [members, setMembers]   = useState([]);
  const [loadingM, setLoadingM] = useState(true);
  const [revealed, setRevealed] = useState({});
  const [copied,   setCopied]   = useState({});
  const [resetModal, setResetModal] = useState(null);
  const [resetPw,    setResetPw]    = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetMsg,   setResetMsg]   = useState({});

  useEffect(() => {
    api.get('/team')
      .then(r => setMembers(r.data?.data || r.data || []))
      .catch(() => {})
      .finally(() => setLoadingM(false));
  }, []);

  /* Clients for View As */
  const [clients, setClients] = useState([]);
  useEffect(() => {
    if (can && can('*')) {
      api.get('/clients').then(r => setClients(r.data?.data || r.data || [])).catch(() => {});
    }
  }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(p => ({...p, [key]: true}));
      setTimeout(() => setCopied(p => ({...p, [key]: false})), 1500);
    });
  };

  const doReset = async () => {
    if (!resetPw || resetPw.length < 6)
      return setResetMsg({ [resetModal.id]: { text:'Min 6 characters.', ok:false } });
    setResetSaving(true);
    try {
      if (resetModal.isClient) {
        await api.post(`/clients/${resetModal.id}/reset-password`, { new_password: resetPw });
        setClients(prev => prev.map(c => c.id === resetModal.id ? {...c, plain_password: resetPw} : c));
      } else {
        await api.post(`/users/${resetModal.id}/reset-password`, { new_password: resetPw });
        setMembers(prev => prev.map(m => m.id === resetModal.id ? {...m, plain_password: resetPw} : m));
      }
      setResetModal(null); setResetPw('');
    } catch (err) {
      setResetMsg({ [resetModal.id]: { text: err?.response?.data?.message || 'Reset failed.', ok:false } });
    } finally { setResetSaving(false); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Change own password */}
      <div className="card">
        <div className="card-header"><div className="card-title">🔑 Change Your Password</div></div>
        {pwMsg.text && <div style={alertStyle(pwMsg.ok)}>{pwMsg.text}</div>}
        <form onSubmit={changePassword}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <div style={{ position:'relative' }}>
                <input type={showPw.current ? 'text' : 'password'} className="form-input" required
                  value={pwForm.current_password}
                  onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
                  placeholder="Enter current password"
                  style={{ paddingRight:36 }} />
                <button type="button" onClick={() => setShowPw(p => ({...p, current: !p.current}))}
                  style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'1rem', lineHeight:1, color:'var(--text-muted)' }}>
                  {showPw.current ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div style={{ position:'relative' }}>
                <input type={showPw.newpw ? 'text' : 'password'} className="form-input" required
                  value={pwForm.new_password}
                  onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
                  placeholder="Min 6 characters"
                  style={{ paddingRight:36 }} />
                <button type="button" onClick={() => setShowPw(p => ({...p, newpw: !p.newpw}))}
                  style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'1rem', lineHeight:1, color:'var(--text-muted)' }}>
                  {showPw.newpw ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <div style={{ position:'relative' }}>
                <input type={showPw.confirm ? 'text' : 'password'} className="form-input" required
                  value={pwForm.confirm_password}
                  onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})}
                  placeholder="Repeat new password"
                  style={{ paddingRight:36 }} />
                <button type="button" onClick={() => setShowPw(p => ({...p, confirm: !p.confirm}))}
                  style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'1rem', lineHeight:1, color:'var(--text-muted)' }}>
                  {showPw.confirm ? '🙈' : '👁'}
                </button>
              </div>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={pwSaving}>
            {pwSaving ? 'Saving...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Change email — owners only */}
      {user?.role === 'owner' && (
        <div className="card">
          <div className="card-header"><div className="card-title">✉️ Change Your Email</div></div>
          <div className="text-sm text-muted" style={{ marginBottom:16 }}>
            Your current login email is <strong>{user?.email}</strong>. Changing it will log you out immediately — you'll need to sign in with the new email.
          </div>
          {emailMsg.text && <div style={alertStyle(emailMsg.ok)}>{emailMsg.text}</div>}
          <form onSubmit={changeEmail}>
            <div className="form-group" style={{ maxWidth:380 }}>
              <label className="form-label">New Email Address</label>
              <input
                type="email"
                className="form-input"
                required
                value={newEmail}
                onChange={e => { setNewEmail(e.target.value); setEmailMsg({ text:'', ok:true }); }}
                placeholder="new@yourdomain.com"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={emailSaving || !newEmail.trim()}>
              {emailSaving ? 'Updating...' : 'Update Email'}
            </button>
          </form>
        </div>
      )}

      {/* Team member credentials */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">👥 Team Member Credentials</div>
          <span className="badge">{members.length} members</span>
        </div>
        <div className="text-sm text-muted" style={{ marginBottom:16 }}>
          View and reset passwords for your team. Only visible to account owners.
        </div>

        {loadingM ? (
          <div className="text-sm text-muted">Loading team members...</div>
        ) : members.length === 0 ? (
          <div className="text-sm text-muted">No team members found.</div>
        ) : (
          <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
              <thead>
                <tr style={{ background:'var(--bg-alt,#f8fafc)', borderBottom:'1px solid var(--border)' }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Login ID</th>
                  <th style={thStyle}>Password</th>
                  <th style={{...thStyle, width:120, textAlign:'center'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...members].sort((a,b) => { if (a.role === 'owner' && b.role !== 'owner') return -1; if (a.role !== 'owner' && b.role === 'owner') return 1; return (a.name||'').localeCompare(b.name||''); }).map(m => (
                  <tr key={m.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', background: m.color || 'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:'0.68rem', flexShrink:0 }}>
                          {m.initials || (m.name||'').slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-600">{m.name}</div>
                          <div className="text-xs text-muted">{m.designation || m.role}</div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ color: m.email ? 'inherit' : 'var(--text-muted)' }}>{m.email || '—'}</span>
                        {m.email && <button style={tblIconBtn} onClick={() => copy(m.email, 'e-'+m.id)} title="Copy">{copied['e-'+m.id] ? '✓' : '⧉'}</button>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ letterSpacing: revealed[m.id] ? 0 : 2, fontFamily:'monospace', fontSize:'0.8rem' }}>
                          {revealed[m.id] ? (m.plain_password || '—') : '••••••'}
                        </span>
                        <button style={tblIconBtn} onClick={() => setRevealed(p => ({...p, [m.id]: !p[m.id]}))} title={revealed[m.id] ? 'Hide' : 'Show'}>
                          {revealed[m.id] ? '🙈' : '👁'}
                        </button>
                        {m.plain_password && revealed[m.id] && <button style={tblIconBtn} onClick={() => copy(m.plain_password, 'p-'+m.id)} title="Copy">{copied['p-'+m.id] ? '✓' : '⧉'}</button>}
                      </div>
                    </td>
                    <td style={{...tdStyle, textAlign:'center'}}>
                      <button className="btn btn-secondary" style={{ fontSize:'0.7rem', padding:'3px 10px' }}
                        onClick={() => { setResetModal(m); setResetPw(''); setResetMsg({}); }}>
                        ↺ Reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Client Credentials — only for owners */}
      {can && can('*') && clients.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">🏢 Client Credentials</div>
            <span className="badge">{clients.length} clients</span>
          </div>
          <div className="text-sm text-muted" style={{ marginBottom:16 }}>
            View and manage login credentials for client accounts. Only visible to account owners.
          </div>

          <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
              <thead>
                <tr style={{ background:'var(--bg-alt,#f8fafc)', borderBottom:'1px solid var(--border)' }}>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>Login ID</th>
                  <th style={thStyle}>Password</th>
                  <th style={{...thStyle, width:140, textAlign:'center'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...clients].sort((a,b) => (a.name||'').localeCompare(b.name||'')).map(c => (
                  <tr key={'cl-'+c.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={tdStyle}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:30, height:30, borderRadius:'50%', background: c.color || '#0ea5e9', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:'0.68rem', flexShrink:0 }}>
                          {(c.name||'').slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-600">{c.name}</div>
                          <div className="text-xs text-muted">{c.contact_name || 'Client'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {c.login_email ? (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span>{c.login_email}</span>
                          <button style={tblIconBtn} onClick={() => copy(c.login_email, 'ce-'+c.id)} title="Copy">{copied['ce-'+c.id] ? '✓' : '⧉'}</button>
                        </div>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td style={tdStyle}>
                      {c.login_email ? (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ letterSpacing: revealed['cl-'+c.id] ? 0 : 2, fontFamily:'monospace', fontSize:'0.8rem' }}>
                            {revealed['cl-'+c.id] ? (c.plain_password || '—') : '••••••'}
                          </span>
                          <button style={tblIconBtn} onClick={() => setRevealed(p => ({...p, ['cl-'+c.id]: !p['cl-'+c.id]}))} title={revealed['cl-'+c.id] ? 'Hide' : 'Show'}>
                            {revealed['cl-'+c.id] ? '🙈' : '👁'}
                          </button>
                          {c.plain_password && revealed['cl-'+c.id] && <button style={tblIconBtn} onClick={() => copy(c.plain_password, 'cp-'+c.id)} title="Copy">{copied['cp-'+c.id] ? '✓' : '⧉'}</button>}
                        </div>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td style={{...tdStyle, textAlign:'center'}}>
                      {c.login_email ? (
                        <button className="btn btn-secondary" style={{ fontSize:'0.7rem', padding:'3px 10px' }}
                          onClick={() => { setResetModal({...c, id: c.id, name: c.name, isClient: true}); setResetPw(''); setResetMsg({}); }}>
                          ↺ Reset
                        </button>
                      ) : (
                        <button className="btn btn-primary" style={{ fontSize:'0.7rem', padding:'3px 10px' }}
                          onClick={async () => {
                            if (!c.email) return alert('This client has no email address. Add one in Master Data first.');
                            try {
                              const res = await api.post('/clients/'+c.id+'/create-login');
                              setClients(prev => prev.map(x => x.id === c.id ? {...x, login_email: res.data.login_email, plain_password: res.data.plain_password} : x));
                            } catch(e) { alert(e.response?.data?.error || 'Failed to create login.'); }
                          }}>
                          + Create Login
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View As — only shown to owners */}
      {can && can('*') && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">👁 View As</div>
            <span className="text-xs text-muted" style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Switch perspective to see the system as a team member or client</span>
          </div>

          {/* Team Members */}
          {members.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div className="text-xs text-muted" style={{ fontWeight:600, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.8px', fontSize:'0.68rem' }}>Team Members</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {members.filter(m => m.id !== (members.find(x => x.role==='owner') || {}).id).map(m => (
                  <button key={m.id} onClick={() => startViewAs && startViewAs({ ...m, role: m.role || 'member' })}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', cursor:'pointer', transition:'all 0.15s', fontSize:'0.82rem' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#7c3aed'; e.currentTarget.style.background='rgba(124,58,237,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--bg)'; }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background: m.color || '#7c3aed', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:'0.7rem', flexShrink:0 }}>
                      {m.initials || (m.name||'').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ textAlign:'left' }}>
                      <div style={{ fontWeight:600, color:'var(--text)', fontSize:'0.8rem' }}>{m.name}</div>
                      <div style={{ color:'var(--text-muted)', fontSize:'0.7rem', textTransform:'capitalize' }}>{m.role || 'member'}</div>
                    </div>
                    <span style={{ marginLeft:4, fontSize:'0.7rem', color:'#7c3aed' }}>→</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Clients */}
          {clients.length > 0 && (
            <div>
              <div className="text-xs text-muted" style={{ fontWeight:600, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.8px', fontSize:'0.68rem' }}>Clients</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {clients.slice(0, 20).map(c => (
                  <button key={c.id} onClick={() => startViewAs && startViewAs({ id: c.id, name: c.name, role: 'client', initials: (c.name||'').slice(0,2).toUpperCase(), color: '#0ea5e9', custom_permissions: c.custom_permissions })}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', cursor:'pointer', transition:'all 0.15s', fontSize:'0.82rem' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#0ea5e9'; e.currentTarget.style.background='rgba(14,165,233,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--bg)'; }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:'#0ea5e9', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:'0.7rem', flexShrink:0 }}>
                      {(c.name||'').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ textAlign:'left' }}>
                      <div style={{ fontWeight:600, color:'var(--text)', fontSize:'0.8rem' }}>{c.name}</div>
                      <div style={{ color:'var(--text-muted)', fontSize:'0.7rem' }}>Client</div>
                    </div>
                    <span style={{ marginLeft:4, fontSize:'0.7rem', color:'#0ea5e9' }}>→</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {members.length === 0 && clients.length === 0 && (
            <div className="text-sm text-muted">No team members or clients found.</div>
          )}
        </div>
      )}

      {/* Reset Modal */}
      {resetModal && (
        <div className="modal-overlay open">
          <div className="modal-box" style={{ maxWidth:380 }}>
            <div className="modal-header">
              <div className="modal-title">Reset Password — {resetModal.name}</div>
              <button className="modal-close" onClick={() => setResetModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-input" type="text" autoFocus
                  placeholder="Min 6 characters"
                  value={resetPw}
                  onChange={e => setResetPw(e.target.value)} />
                {resetMsg[resetModal.id] && (
                  <div style={{ fontSize:'0.75rem', color: resetMsg[resetModal.id].ok ? '#22c55e' : '#ef4444', marginTop:4 }}>
                    {resetMsg[resetModal.id].text}
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ padding:0, border:'none', marginTop:8 }}>
                <button className="btn btn-secondary" onClick={() => setResetModal(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={resetSaving || !resetPw.trim()} onClick={doReset}>
                  {resetSaving ? 'Saving...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Communication ── */
function CommunicationTab({ tenant }) {
  const [form, setForm] = useState({
    smtp_host: '', smtp_port: '587', smtp_username: '', smtp_password: '',
    smtp_encryption: 'tls', smtp_from_email: '', smtp_from_name: '',
    whatsapp_number: '',
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [msg, setMsg]           = useState({ text: '', ok: true });
  const [hasPassword, setHasPassword] = useState(false);

  useEffect(() => {
    api.get('/tenant/smtp-settings').then(r => {
      const d = r.data.data || r.data;
      setForm(f => ({
        ...f,
        smtp_host: d.smtp_host || '',
        smtp_port: String(d.smtp_port || 587),
        smtp_username: d.smtp_username || '',
        smtp_encryption: d.smtp_encryption || 'tls',
        smtp_from_email: d.smtp_from_email || '',
        smtp_from_name: d.smtp_from_name || '',
        whatsapp_number: d.whatsapp_number || '',
      }));
      setHasPassword(d.has_password);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const ch = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/tenant/smtp-settings', form);
      setMsg({ text: 'Communication settings saved.', ok: true });
      if (form.smtp_password) setHasPassword(true);
      setForm(f => ({ ...f, smtp_password: '' }));
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'Save failed.', ok: false });
    }
    setSaving(false);
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const r = await api.post('/tenant/smtp-test');
      setMsg({ text: r.data.message, ok: true });
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'Test failed.', ok: false });
    }
    setTesting(false);
  };

  if (loading) return <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <form onSubmit={save}>
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '1rem', color: 'var(--text)' }}>Email (SMTP) Configuration</h3>
        <p style={{ margin: '0 0 18px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Configure the email account used for sending touchpoint emails to clients.</p>

        {msg.text && <div style={alertStyle(msg.ok)}>{msg.text}</div>}

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">SMTP Host</label>
            <input className="form-select" name="smtp_host" value={form.smtp_host} onChange={ch} placeholder="smtp.gmail.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Port</label>
            <input className="form-select" name="smtp_port" value={form.smtp_port} onChange={ch} placeholder="587" style={{ maxWidth: 120 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Encryption</label>
            <select className="form-select" name="smtp_encryption" value={form.smtp_encryption} onChange={ch}>
              <option value="tls">TLS</option>
              <option value="ssl">SSL</option>
              <option value="">None</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Username / Email</label>
            <input className="form-select" name="smtp_username" value={form.smtp_username} onChange={ch} placeholder="you@company.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Password / App Password</label>
            <input className="form-select" name="smtp_password" value={form.smtp_password} onChange={ch} type="password"
              placeholder={hasPassword ? '••••••••  (leave blank to keep)' : 'Enter password'} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">From Email</label>
            <input className="form-select" name="smtp_from_email" value={form.smtp_from_email} onChange={ch} placeholder="noreply@company.com" />
          </div>
          <div className="form-group">
            <label className="form-label">From Name</label>
            <input className="form-select" name="smtp_from_name" value={form.smtp_from_name} onChange={ch} placeholder={tenant?.name || 'Agency Name'} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
          <button className="btn" type="button" onClick={testEmail} disabled={testing}
            style={{ background: 'var(--bg2)', color: 'var(--text)' }}>{testing ? 'Sending...' : 'Send Test Email'}</button>
        </div>
      </div>

      <div className="card" style={{ padding: 24, marginTop: 16 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '1rem', color: 'var(--text)' }}>WhatsApp Number</h3>
        <p style={{ margin: '0 0 18px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>WhatsApp messages currently open in the sender\'s own WhatsApp app. Set your agency number here for reference.</p>
        <div className="form-group" style={{ maxWidth: 350 }}>
          <label className="form-label">WhatsApp Number</label>
          <input className="form-select" name="whatsapp_number" value={form.whatsapp_number} onChange={ch} placeholder="+1234567890" />
        </div>
      </div>

      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 16 }}>
        <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <strong>Gmail:</strong> Use smtp.gmail.com, port 587, TLS. Generate an App Password at myaccount.google.com → Security → App Passwords.<br/>
          <strong>Company domain:</strong> Use your mail provider\'s SMTP host (e.g. smtp.office365.com for Outlook, smtp.zoho.com for Zoho).
        </p>
      </div>
    </form>
  );
}

/* ── Danger Zone: delete agency account (Owner only) ── */
function DangerZoneTab({ tenant, user, logout }) {
  const [agencyName, setAgencyName] = useState('');
  const [password, setPassword]     = useState('');
  const [code, setCode]             = useState('');
  const [codeSent, setCodeSent]     = useState(false);
  const [busy, setBusy]             = useState('');
  const [msg, setMsg]               = useState(null);   // { ok, text }
  const [done, setDone]             = useState(false);

  const nameOk = agencyName.trim().toLowerCase() === (tenant?.name || '').trim().toLowerCase() && agencyName.trim() !== '';
  const canDelete = nameOk && password && code.trim().length === 6 && codeSent && !busy;
  const errText = (e, fallback) => e.response?.data?.error || e.response?.data?.message || fallback;

  const downloadData = async () => {
    setBusy('export'); setMsg(null);
    try {
      const res = await api.get('/tenant/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(tenant?.name || 'agency').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase()}-qlicxo-export.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg({ ok: false, text: 'Could not download the data. Please try again.' });
    } finally { setBusy(''); }
  };

  const sendCode = async () => {
    setBusy('code'); setMsg(null);
    try {
      const res = await api.post('/tenant/deletion/send-code');
      setCodeSent(true);
      setMsg({ ok: true, text: res.data.message });
    } catch (e) {
      setMsg({ ok: false, text: errText(e, 'Could not send the code. Please try again.') });
    } finally { setBusy(''); }
  };

  const confirmDelete = async () => {
    if (!canDelete) return;
    setBusy('delete'); setMsg(null);
    try {
      const res = await api.post('/tenant/deletion/request', { agency_name: agencyName, password, code: code.trim() });
      setDone(true);
      setMsg({ ok: true, text: res.data.message });
      setTimeout(() => { logout(); window.location.href = '/login'; }, 8000);
    } catch (e) {
      setMsg({ ok: false, text: errText(e, 'Could not delete the account. Please try again.') });
    } finally { setBusy(''); }
  };

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:8, background:'var(--bg)', color:'var(--text)', fontSize:'0.85rem', boxSizing:'border-box' };
  const labelStyle = { display:'block', fontSize:'0.78rem', fontWeight:600, color:'var(--text)', marginBottom:6 };

  return (
    <div className="card" style={{ border:'1px solid #ef4444' }}>
      <div className="card-header"><div className="card-title" style={{ color:'#ef4444' }}>⚠️ Delete Agency Account</div></div>
      <div style={{ padding:'4px 20px 20px' }}>
        <div style={{ fontSize:'0.85rem', color:'var(--text)', lineHeight:1.7, marginBottom:16 }}>
          This deletes <strong>{tenant?.name}</strong> and <strong>all</strong> of its data — team members, clients, work orders, content, files and Master Data.
          <ul style={{ margin:'8px 0 0', paddingLeft:20 }}>
            <li>The account is switched off <strong>immediately</strong> and everyone is logged out.</li>
            <li>You have <strong>30 days</strong> to change your mind using the <strong>Cancel Deletion</strong> link we email you.</li>
            <li>After 30 days everything is <strong>permanently deleted</strong> and cannot be recovered.</li>
          </ul>
        </div>

        {msg && <div style={alertStyle(msg.ok)}>{msg.text}{done && <><br/>You will be logged out shortly.</>}</div>}

        {!done && (
          <>
            <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:14, marginBottom:18 }}>
              <div style={{ fontSize:'0.82rem', fontWeight:600, marginBottom:6 }}>Step 1 — Download your data first (recommended)</div>
              <button type="button" className="btn btn-secondary" onClick={downloadData} disabled={!!busy}>
                {busy === 'export' ? 'Preparing…' : '⬇ Download all data'}
              </button>
            </div>

            <div style={{ fontSize:'0.82rem', fontWeight:600, marginBottom:10 }}>Step 2 — Confirm it's you</div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Type the agency name <span style={{ color:'#ef4444' }}>{tenant?.name}</span> to confirm</label>
              <input style={inputStyle} value={agencyName} onChange={e => setAgencyName(e.target.value)} placeholder={tenant?.name} autoComplete="off" />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Your password</label>
              <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
            </div>

            <div style={{ marginBottom:18 }}>
              <label style={labelStyle}>6-digit code sent to {user?.email}</label>
              <div style={{ display:'flex', gap:8 }}>
                <input style={{ ...inputStyle, maxWidth:180, letterSpacing:4 }} value={code} maxLength={6}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" disabled={!codeSent} inputMode="numeric" />
                <button type="button" className="btn btn-secondary" onClick={sendCode} disabled={!!busy}>
                  {busy === 'code' ? 'Sending…' : codeSent ? 'Resend code' : 'Send code'}
                </button>
              </div>
            </div>

            <button type="button" onClick={confirmDelete} disabled={!canDelete}
              style={{ padding:'10px 20px', border:'none', borderRadius:8, fontWeight:700, fontSize:'0.85rem',
                       background: canDelete ? '#ef4444' : '#fca5a5', color:'#fff', cursor: canDelete ? 'pointer' : 'not-allowed' }}>
              {busy === 'delete' ? 'Deleting…' : '🗑 Delete Agency Account'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── helpers ── */
function alertStyle(ok) {
  return {
    padding:'10px 14px', borderRadius:8, fontSize:'0.82rem', marginBottom:14,
    background: ok ? '#f0fdf4' : '#fef2f2',
    border: `1px solid ${ok ? '#22c55e' : '#ef4444'}`,
    color: ok ? '#16a34a' : '#b91c1c',
  };
}

const thStyle = {
  textAlign:'left', padding:'10px 14px', fontSize:'0.75rem',
  fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase',
  letterSpacing:'0.04em',
};
const tdStyle = {
  padding:'10px 14px', verticalAlign:'middle',
};
const tblIconBtn = {
  background:'transparent', border:'none', cursor:'pointer',
  color:'var(--text-muted)', fontSize:'0.78rem', padding:'2px 4px', lineHeight:1,
};
